import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Music, Phone } from "lucide-react";
import { MaskedLine, Reveal, Btn, Overline } from "@/components/motion-bits";
import { Marquee } from "@/components/Marquee";
import heroImg from "@/assets/hero_bridge_truck.jpg";
import riverImg from "@/assets/about_memphis_river.jpg";
import regionalImg from "@/assets/service_regional.jpg";
import otrImg from "@/assets/service_otr.jpg";
import logoImg from "@/assets/logo.png";

const FACTS = [
  { k: "Established", v: "2025" },
  { k: "The Crossing", v: "I-55 × I-40" },
  { k: "Regional", v: "Home every weekend" },
  { k: "Over the Road", v: "2 weeks out · 3 days home" },
];

const DIVISIONS = [
  {
    id: "regional",
    tag: "Division 01",
    title: "REGIONAL",
    hook: "Home every weekend.",
    copy: "Consistent regional freight out of Memphis — run your lanes through the week and be home when the weekend hits.",
    img: regionalImg,
    alt: "Illustration of a semi truck driving toward a farmhouse at dusk",
  },
  {
    id: "otr",
    tag: "Division 02",
    title: "OVER THE ROAD",
    hook: "Two weeks out. Three days home.",
    copy: "Long-haul miles with a rhythm you can plan a life around — fourteen days on the road, then three full days at home.",
    img: otrImg,
    alt: "Illustration of a semi truck on an open interstate under a starry sky",
  },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(heroProgress, [0, 1], ["0%", "18%"]);

  const crossRef = useRef(null);
  const { scrollYProgress: crossProgress } = useScroll({
    target: crossRef,
    offset: ["start end", "end start"],
  });
  const crossY = useTransform(crossProgress, [0, 1], ["-8%", "8%"]);

  return (
    <div data-testid="home-page">
      {/* ---------- Kinetic hero ---------- */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-end overflow-hidden bg-ink" data-testid="home-hero">
        <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
          <img
            src={heroImg}
            alt=""
            className="h-full w-full object-cover"
            initial={{ scale: 1.18 }}
            animate={{ scale: 1.08 }}
            transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }}
          />
        </motion.div>
        <div className="absolute inset-0 bg-black/45" aria-hidden="true" />

        <div className="relative w-full max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-16 md:pt-40 md:pb-24">
          <Overline className="text-brand-gold">
            <MaskedLine delay={0.15}>Memphis, Tennessee — Est. 2025</MaskedLine>
          </Overline>

          <h1 className="mt-4 font-display uppercase tracking-tight leading-[0.88] text-cream text-[clamp(3.8rem,11vw,9.5rem)]">
            <MaskedLine delay={0.28}>Memphis</MaskedLine>
            <MaskedLine delay={0.42}>
              <span className="text-brand-gold">Viaduct</span> <span className="text-cream/90">Inc.</span>
            </MaskedLine>
          </h1>

          <div className="mt-6 md:mt-8">
            <MaskedLine delay={0.6}>
              <span className="inline-block -rotate-1 border-[3px] border-ink bg-brand-red px-4 py-2 font-display uppercase tracking-wide text-xl md:text-3xl text-cream shadow-[6px_6px_0_#111]">
                Your Road to Reliance
              </span>
            </MaskedLine>
          </div>

          <Reveal delay={0.75} y={18}>
            <p className="mt-8 max-w-xl text-lg md:text-xl leading-relaxed text-cream/85">
              Regional &amp; over-the-road trucking out of the crossing point of I-55 &amp; I-40,
              on the bank of the Mississippi River.
            </p>
          </Reveal>

          <Reveal delay={0.88} y={18}>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Btn to="/contact" variant="gold" testid="home-ship-cta">
                Ship With Us <ArrowUpRight size={20} aria-hidden="true" />
              </Btn>
              <Btn to="/careers" variant="cream" testid="home-drive-cta">
                Drive With Us <ArrowUpRight size={20} aria-hidden="true" />
              </Btn>
              <a
                href="tel:+18338123828"
                data-testid="home-phone-chip"
                className="inline-flex items-center gap-2 border-b-[3px] border-brand-gold pb-1 font-display uppercase tracking-wide text-lg text-cream transition-colors duration-200 hover:text-brand-gold"
              >
                <Phone size={18} aria-hidden="true" /> 833-V1aduct
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- Editorial marquee ---------- */}
      <Marquee
        className="bg-brand-red text-cream"
        items={[
          "Your Road to Reliance",
          "Memphis Viaduct Inc",
          "I-55 & I-40",
          "Regional",
          "Over the Road",
          "Memphis, Tennessee",
        ]}
      />

      {/* ---------- Company statement ---------- */}
      <section className="px-6 lg:px-10 py-20 md:py-28">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-10 md:gap-8">
          <div className="md:col-span-7">
            <Reveal>
              <Overline className="text-brand-red">The Company</Overline>
              <h2 className="mt-4 font-display uppercase tracking-tight text-4xl md:text-6xl leading-[0.95]">
                Two divisions.
                <br />
                One promise: <span className="text-brand-teal">we get it there.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-8 max-w-xl text-lg leading-relaxed text-ink/75">
                Memphis Viaduct Inc runs regional and over-the-road freight out of Memphis,
                Tennessee — the spot on the Mississippi River bank where I-55 and I-40 cross.
                Whether your load loops the region or crosses the country, it moves on a
                schedule we keep, with a dispatch that picks up the phone.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8">
                <Btn to="/about" variant="ink" testid="home-about-cta">Our Story <ArrowUpRight size={20} aria-hidden="true" /></Btn>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="md:col-span-4 md:col-start-9">
            <div className="rotate-2 border-[3px] border-ink bg-white p-2 shadow-blocklg transition-transform duration-300 hover:rotate-0">
              <img
                src={logoImg}
                alt="Memphis Viaduct Inc badge — the Memphis Bridge, the Pyramid, blues notes and a saxophone around a semi truck"
                className="w-full object-cover"
                data-testid="home-logo-sticker"
              />
              <p className="px-2 py-3 text-center font-display uppercase tracking-wide text-lg text-brand-red">
                Est. 2025 · Memphis, TN
              </p>
            </div>
          </Reveal>
        </div>

        <div className="max-w-7xl mx-auto mt-16 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-10">
          {FACTS.map((f, i) => (
            <Reveal key={f.k} delay={0.08 * i}>
              <div className="border-t-[3px] border-ink pt-4">
                <Overline className="text-ink/50">{f.k}</Overline>
                <p className="mt-2 font-display uppercase tracking-tight text-2xl md:text-3xl">{f.v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- Divisions ---------- */}
      <section className="bg-brand-tealdeep text-cream px-6 lg:px-10 py-20 md:py-28 border-y-[3px] border-ink">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <Overline className="text-brand-gold">Services</Overline>
            <h2 className="mt-4 font-display uppercase tracking-tight text-4xl md:text-6xl">Pick your lane.</h2>
          </Reveal>

          <div className="mt-12 grid md:grid-cols-2 gap-8 lg:gap-12">
            {DIVISIONS.map((d, i) => (
              <Reveal key={d.id} delay={0.1 * i}>
                <Link
                  to="/services"
                  data-testid={`home-division-${d.id}-card`}
                  className="group block border-[3px] border-ink bg-cream text-ink shadow-[8px_8px_0_#000] transition-[transform,box-shadow] duration-200 hover:-translate-y-1.5 hover:shadow-[12px_12px_0_#000]"
                >
                  <div className="overflow-hidden border-b-[3px] border-ink">
                    <img
                      src={d.img}
                      alt={d.alt}
                      className="w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6 md:p-8">
                    <Overline className="text-brand-red">{d.tag}</Overline>
                    <h3 className="mt-2 font-display uppercase tracking-tight text-3xl md:text-4xl">{d.title}</h3>
                    <p className="mt-2 font-display uppercase tracking-tight text-2xl text-brand-teal">{d.hook}</p>
                    <p className="mt-3 leading-relaxed text-ink/70">{d.copy}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-brand-red">
                      See the lane <ArrowUpRight size={18} aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- The crossing point ---------- */}
      <section ref={crossRef} className="bg-ink text-cream px-6 lg:px-10 py-20 md:py-28">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <Reveal>
              <Overline className="text-brand-gold">The Crossing Point</Overline>
              <h2 className="mt-4 font-display uppercase tracking-tight text-4xl md:text-6xl leading-[0.95]">
                Memphis sits where the map pinches.
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-8 text-lg leading-relaxed text-cream/75 max-w-xl">
                Our home base sits on the bank of the Mississippi River at the I-55 &amp; I-40
                crossing — the interchange where north-south river traffic meets the east-west
                interstates. From Memphis, freight pulls in every compass direction, and it all
                starts across the bridge on our badge.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-lg leading-relaxed text-cream/75 max-w-xl">
                That crossing point is why we're reliable when it matters: shorter detours,
                tighter handoffs, and a dispatch that knows every mile between here and there.
              </p>
            </Reveal>
            <Reveal delay={0.28}>
              <div className="mt-8">
                <Btn to="/contact" variant="gold" testid="home-location-cta">
                  Move Freight With Us <ArrowUpRight size={20} aria-hidden="true" />
                </Btn>
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-6">
            <Reveal delay={0.15}>
              <div className="-rotate-1 border-[3px] border-brand-gold bg-cream p-2 shadow-blocklg">
                <div className="overflow-hidden">
                  <motion.img
                    src={riverImg}
                    alt="Illustration of the Memphis riverfront — bridge, pyramid and a blues saxophone player on the Mississippi bank"
                    style={{ y: crossY }}
                    className="w-full aspect-square object-cover scale-[1.18]"
                    data-testid="home-crossing-illustration"
                  />
                </div>
                <p className="flex items-center justify-center gap-2 px-2 py-3 text-center font-display uppercase tracking-wide text-lg text-ink">
                  <Music size={18} className="text-brand-red" aria-hidden="true" /> Memphis, Tennessee
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- CTA band ---------- */}
      <section className="bg-brand-gold border-b-[3px] border-ink px-6 lg:px-10 py-16 md:py-20">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <Reveal>
            <h2 className="font-display uppercase tracking-tight text-5xl md:text-7xl text-ink">Ready to roll?</h2>
            <p className="mt-3 text-lg font-semibold text-ink/70">
              For more info call 833-V1aduct (833-812-3828).
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="flex flex-wrap gap-4">
              <Btn to="/contact" variant="ink" testid="home-footer-ship-cta">Ship With Us</Btn>
              <Btn to="/careers" variant="red" testid="home-footer-drive-cta">Drive With Us</Btn>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
